import { useCallback, useEffect, useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet.jsx';
import { getPresentOptions } from '@/services/quoteIngest.service.js';
import { buildQuoteComparison, differingRows } from '@/lib/quoteCompare.js';
import { fmtMoney } from '@/lib/quoteFields.js';

// COMPARE — the offers on this deal, against each other, field by field.
//
// The question this answers is NOT the one off-applied-terms answers. That one
// asks "did this carrier quote what I asked for?", and on N8095M every quote
// matched the application exactly while the offers differed materially:
//
//   Beacon  $3,338   $2,500 NIM / $7,500 IM
//   Starr   $3,328   $0 / $0
//
// Ten dollars apart and a materially better policy. A rank by premium puts them
// one line apart and says nothing, which is why THIS SHEET DECLARES NO WINNER
// and offers no ordering. It shows the differences; David decides.
//
// It renders. It derives nothing: the comparison is lib/quoteCompare.js, which
// the node deploy gate imports directly (tests/quote-compare.test.js).
//
// Data comes from the EXISTING present-options endpoint, which already returns
// the full option grain including both deductibles. No new door.

function cellText(row, v) {
  if (!v.stated) return 'not stated';
  const raw = v.raw;
  switch (row.kind) {
    case 'money': {
      const n = Number(String(raw).replace(/[,$\s]/g, ''));
      return Number.isFinite(n) ? fmtMoney(n) : String(raw);
    }
    case 'deductible': {
      const s = String(raw).trim();
      if (/%$/.test(s)) return s;
      const n = Number(s.replace(/[,$\s]/g, ''));
      return Number.isFinite(n) ? fmtMoney(n) : s;
    }
    case 'bool':
      return raw === true || raw === 'true' ? 'Yes' : 'No';
    case 'number':
      return String(raw);
    default:
      return String(raw);
  }
}

export default function CompareQuotesSheet({ dealId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [onlyDiffs, setOnlyDiffs] = useState(true);

  const load = useCallback(() => {
    if (!dealId || !open) return;
    setData(null);
    setError('');
    getPresentOptions(dealId)
      .then((d) => setData(d))
      .catch((err) => setError(err.body?.error || err.message || 'Load failed'));
  }, [dealId, open]);

  useEffect(() => { load(); }, [load]);

  // One column per OPTION, not per quote: a carrier quoting three options is
  // three offers and the broker compares offers. Excluded options are dropped
  // (the broker already ruled them out); nothing else is filtered, ranked or
  // reordered — the server's order is kept exactly as returned.
  const columns = [];
  for (const q of data?.quotes || []) {
    for (const o of q.options || []) {
      if (o.excluded === true) continue;
      columns.push({
        id: o.id,
        carrier: q.carrier_display || q.carrier || 'Carrier',
        label: o.option_label || null,
        option: { ...o, term_months: o.term_months ?? q.term_months },
      });
    }
  }

  const comparison = buildQuoteComparison(columns);
  const diffs = differingRows(comparison);
  const rows = onlyDiffs ? diffs : comparison.rows;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* The base right-side variant is `sm:max-w-md` (28rem), which silently
          won over a plain `max-w-4xl` and pushed the third carrier off-screen —
          hiding the exact column the comparison exists for. Match the breakpoint
          so this wins, and let the table scroll only past four offers. */}
      <SheetContent side="right" className="w-full sm:max-w-5xl">
        <SheetHeader>
          <SheetTitle>Compare quotes</SheetTitle>
          <SheetDescription>
            {columns.length > 1
              ? `${columns.length} offers, ${diffs.length} field${diffs.length === 1 ? '' : 's'} where they differ. No ranking; the differences are the point.`
              : 'Every offer on this deal, side by side.'}
          </SheetDescription>
        </SheetHeader>

        {error ? (
          <p className="mt-6 text-sm text-danger">{error}</p>
        ) : data === null ? (
          <p className="mt-6 text-sm text-text-muted">Reading the quotes…</p>
        ) : columns.length < 2 ? (
          <div className="mt-6 rounded-card border border-dashed border-border p-6 text-sm text-text-muted">
            {columns.length === 0
              ? 'No quotes on this deal yet.'
              : 'Only one offer on this deal. A comparison needs at least two.'}
          </div>
        ) : (
          <>
            <label className="mt-3 flex w-fit items-center gap-2 text-sm text-text-muted">
              <input
                type="checkbox"
                checked={onlyDiffs}
                onChange={(e) => setOnlyDiffs(e.target.checked)}
                className="h-4 w-4 accent-current"
              />
              Only show fields that differ
            </label>

            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[36rem] border-collapse text-sm">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 bg-surface py-2 pr-3 text-left text-[11px] font-semibold uppercase tracking-wide text-text-dim">
                      Field
                    </th>
                    {columns.map((c) => (
                      <th key={c.id} className="px-3 py-2 text-left text-[13px] font-semibold text-text">
                        {c.carrier}
                        {c.label ? <span className="block text-[11px] font-normal text-text-muted">{c.label}</span> : null}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length + 1} className="py-4 text-sm text-text-muted">
                        These offers are identical on every field compared.
                      </td>
                    </tr>
                  ) : rows.map((row) => (
                    <tr key={row.key} className="border-t border-border/60">
                      <td className="sticky left-0 z-10 bg-surface py-2 pr-3 text-[12px] text-text-muted">
                        {row.label}
                      </td>
                      {row.values.map((v, i) => (
                        <td
                          key={columns[i].id}
                          className={`px-3 py-2 tabular-nums ${row.differs ? 'font-medium text-text' : 'text-text-muted'} ${v.stated ? '' : 'italic text-text-dim'}`}
                        >
                          {cellText(row, v)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="mt-3 text-[11px] leading-relaxed text-text-dim">
              A stated $0 and a blank are different answers and read differently here: a nil
              deductible is a term the carrier gave you, and &ldquo;not stated&rdquo; means nobody
              said. Premiums are pre-tax.
            </p>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
